import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import type { Mode } from "./Root";
import { LanguageSwitch } from "./LanguageSwitch";
// @ts-ignore: CSS side-effect import without type declarations
import "./styles/landing.css";

/* eslint-disable i18next/no-literal-string -- the product name, decorative symbols and catalogue key ids, the same in
   every language */
const BRAND = "LexSide";
const SYMBOL = { arrow: "→", bullet: "·", moon: "☾", sun: "☀", where: "↳" } as const;
/* eslint-enable i18next/no-literal-string */

/** Hero signature: a working prompt that seals real values into de-id tokens on a loop. */
function DeidReveal() {
  const { t } = useTranslation("landing");
  const [deid, setDeid] = useState(false);
  useEffect(() => {
    const reduce = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (reduce) {
      setDeid(true);
      return;
    }
    const id = window.setInterval(() => setDeid((d) => !d), 2600);
    return () => window.clearInterval(id);
  }, []);

  const swap = (real: string, tok: string) => (
    <span className={`lp-swap${deid ? " is-deid" : ""}`}>
      <span className="lp-real">{real}</span>
      <span className="lp-tok">{tok}</span>
    </span>
  );

  return (
    <div className="lp-deid lp-rise d3" aria-hidden="true">
      <div className="lp-deid-head">
        <span>{t("demo.head")}</span>
        <span className="z">{deid ? t("demo.deidentified") : t("demo.trustedSide")}</span>
      </div>
      <div className="lp-deid-line">
        {/* The tokens are the de-identifier's own placeholders, the same in every language. */}
        <Trans
          t={t}
          i18nKey="demo.prompt"
          components={{
            person: swap(t("demo.person"), "PERSON_1"),
            case: swap(t("demo.case"), "CASE_1"),
            place: swap(t("demo.place"), "LOCATION_1"),
          }}
        />
      </div>
      <div className="lp-deid-foot">
        <span className="dot" />
        {t("demo.flow.retrieve")}<span className="arrow">{SYMBOL.arrow}</span>{t("demo.flow.deidentify")}
        <span className="arrow">{SYMBOL.arrow}</span>{t("demo.flow.inference")}
        <span className="arrow">{SYMBOL.arrow}</span>{t("demo.flow.reidentify")}
        <span className="dot" />
      </div>
    </div>
  );
}

/* eslint-disable i18next/no-literal-string -- each card's id is its catalogue key; the glyphs are decoration */
const capabilities = [
  { k: "confidentiality", glyph: "⛨" },
  { k: "retrieval", glyph: "▤" },
  { k: "agent", glyph: "◉" },
  { k: "intake", glyph: "✉" },
  { k: "ethicalWalls", glyph: "§" },
  { k: "egressGate", glyph: "⚖" },
] as const;

const topologies = [
  { step: "01", k: "cloud" },
  { step: "02", k: "lawyer" },
  { step: "03", k: "firm" },
] as const;
/* eslint-enable i18next/no-literal-string */

export function Home({
  mode,
  onToggleTheme,
}: {
  mode: Mode;
  onToggleTheme: () => void;
}) {
  const { t } = useTranslation("landing");
  return (
    <div className="lp">
      <nav className="lp-nav">
        <div className="lp-wrap lp-nav-in">
          <a className="lp-brand" href="#top">
            <span className="lp-mark">L</span>
            <span className="lp-name">{BRAND}</span>
          </a>
          <div className="lp-links">
            <a href="#boundary">{t("nav.howItWorks")}</a>
            <a href="#capabilities">{t("nav.capabilities")}</a>
            <a href="#deploy">{t("nav.deployments")}</a>
            <a href="#trust">{t("nav.confidentiality")}</a>
          </div>
          <div className="lp-nav-actions">
            <LanguageSwitch className="lp-icon-btn" plain />
            <button
              className="lp-icon-btn"
              onClick={onToggleTheme}
              aria-label={mode === "light" ? t("nav.themeDark") : t("nav.themeLight")}
              title={t("nav.toggleTheme")}
            >
              {mode === "light" ? SYMBOL.moon : SYMBOL.sun}
            </button>
            <a className="lp-btn ghost" href="/login">
              {t("nav.signIn")}
            </a>
            <a className="lp-btn seal" href="#request">
              {t("cta.requestAccess")}
            </a>
          </div>
        </div>
      </nav>

      {/* hero */}
      <header className="lp-hero" id="top">
        <div className="lp-wrap lp-hero-grid">
          <div>
            <p className="lp-eyebrow lp-rise">
              {t("hero.eyebrow")}
            </p>
            <h1 className="lp-h1 lp-rise d1">
              {t("hero.title")}
            </h1>
            <p className="lp-lede lp-rise d2" style={{ marginTop: 22 }}>
              {t("hero.lede")}
            </p>
            <div className="lp-cta-row lp-rise d2">
              <a className="lp-btn seal" href="#request">
                {t("cta.requestAccess")}
              </a>
              <a className="lp-btn ghost" href="#boundary">
                {t("hero.seeBoundary")}
              </a>
            </div>
            <div className="lp-trustline lp-rise d3">
              <span>
                <i />
                {t("hero.trust.noData")}
              </span>
              <span>
                <i />
                {t("hero.trust.egress")}
              </span>
              <span>
                <i />
                {t("hero.trust.anywhere")}
              </span>
            </div>
          </div>
          <DeidReveal />
        </div>
      </header>

      {/* the boundary (dark band) */}
      <section className="lp-band" id="boundary">
        <div className="lp-wrap lp-section">
          <p className="lp-eyebrow">{t("boundary.eyebrow")}</p>
          <h2 className="lp-h2">
            {t("boundary.title")}
          </h2>
          <p className="lp-lede" style={{ marginTop: 14 }}>
            {t("boundary.lede")}
          </p>

          <div className="lp-flow">
            <div className="lp-zone trusted">
              <h4>{t("boundary.trusted.title")}</h4>
              <p>{t("boundary.trusted.intro")}</p>
              <ol className="lp-steps">
                <li>
                  <span className="n">1</span>
                  <span>
                    {t("boundary.trusted.retrieve")}
                  </span>
                </li>
                <li>
                  <span className="n">2</span>
                  <span>
                    {t("boundary.trusted.deidentify")}
                  </span>
                </li>
                <li>
                  <span className="n">4</span>
                  <span>
                    {t("boundary.trusted.reidentify")}
                  </span>
                </li>
              </ol>
            </div>
            <div className="lp-boundary-rail">
              <span className="rail" />
              <span className="lp-boundary-label">{t("boundary.rail")}</span>
            </div>
            <div className="lp-zone cloud">
              <h4>{t("boundary.cloud.title")}</h4>
              <p>{t("boundary.cloud.intro")}</p>
              <ol className="lp-steps">
                <li>
                  <span className="n">3</span>
                  <span>
                    <Trans t={t} i18nKey="boundary.cloud.reason" components={{ em: <em /> }} />
                  </span>
                </li>
                <li>
                  <span className="n">{SYMBOL.bullet}</span>
                  <span>{t("boundary.cloud.noData")}</span>
                </li>
                <li>
                  <span className="n">{SYMBOL.bullet}</span>
                  <span>
                    {t("boundary.cloud.outbound")}
                  </span>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* capabilities */}
      <section className="lp-section" id="capabilities">
        <div className="lp-wrap">
          <div className="lp-sec-head">
            <p className="lp-eyebrow">{t("capabilities.eyebrow")}</p>
            <h2 className="lp-h2">
              {t("capabilities.title")}
            </h2>
          </div>
          <div className="lp-caps">
            {capabilities.map((c) => (
              <article className="lp-cap" key={c.k}>
                <span className="glyph">{c.glyph}</span>
                <div className="k" style={{ marginTop: 12 }}>
                  {t(`capabilities.${c.k}.kicker`)}
                </div>
                <h3>{t(`capabilities.${c.k}.title`)}</h3>
                <p>{t(`capabilities.${c.k}.body`)}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* deployments */}
      <section
        className="lp-section"
        id="deploy"
        style={{
          background: "var(--surface-alt)",
          borderBlock: "1px solid var(--border)",
        }}
      >
        <div className="lp-wrap">
          <div className="lp-sec-head">
            <p className="lp-eyebrow">{t("deploy.eyebrow")}</p>
            <h2 className="lp-h2">
              {t("deploy.title")}
            </h2>
            <p className="lp-lede" style={{ marginTop: 14 }}>
              {t("deploy.lede")}
            </p>
          </div>
          <div className="lp-topos">
            {topologies.map((topology) => (
              <article className="lp-topo" key={topology.step}>
                <span className="step">{topology.step}</span>
                <h3>{t(`deploy.${topology.k}.title`)}</h3>
                <div className="where">{SYMBOL.where} {t(`deploy.${topology.k}.where`)}</div>
                <p>{t(`deploy.${topology.k}.body`)}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* confidentiality strategies */}
      <section className="lp-section" id="trust">
        <div className="lp-wrap">
          <div className="lp-sec-head">
            <p className="lp-eyebrow">{t("trust.eyebrow")}</p>
            <h2 className="lp-h2">{t("trust.title")}</h2>
            <p className="lp-lede" style={{ marginTop: 14 }}>
              {t("trust.lede")}
            </p>
          </div>
          <div className="lp-strats">
            <div className="lp-strat built">
              <span className="tag">{t("trust.built")}</span>
              <h4>{t("trust.deidentify.title")}</h4>
              <p>{t("trust.deidentify.body")}</p>
            </div>
            <div className="lp-strat soon">
              <span className="tag">{t("trust.onDemand")}</span>
              <h4>{t("trust.private.title")}</h4>
              <p>{t("trust.private.body")}</p>
            </div>
            <div className="lp-strat soon">
              <span className="tag">{t("trust.onDemand")}</span>
              <h4>{t("trust.tee.title")}</h4>
              <p>{t("trust.tee.body")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* final CTA */}
      <section className="lp-band" id="request">
        <div className="lp-wrap lp-cta-final">
          <p className="lp-eyebrow">{t("cta.eyebrow")}</p>
          <h2 className="lp-h2">
            {t("cta.title")}
          </h2>
          <p className="lp-lede">
            {t("cta.lede")}
          </p>
          <div className="lp-cta-row">
            <a
              className="lp-btn seal"
              href={`mailto:${import.meta.env.VITE_SUPPORT_EMAIL}?subject=LexSide%20access`}
            >
              {t("cta.requestAccess")}
            </a>
            <a className="lp-btn on-ink" href="#top">
              {t("cta.backToTop")}
            </a>
          </div>
        </div>
      </section>

      <footer className="lp-footer">
        <div className="lp-wrap lp-footer-in">
          <span className="lp-brand">
            <span
              className="lp-mark"
              style={{ width: 24, height: 24, fontSize: 14 }}
            >
              L
            </span>
            <span className="lp-name" style={{ fontSize: 16 }}>
              {BRAND}
            </span>
          </span>
          <span>{t("footer.tagline")}</span>
          <span className="sep">{t("footer.copyright", { year: new Date().getFullYear() })}</span>
        </div>
      <a href="/subprocessors">{t("footer.subprocessors")}</a></footer>
    </div>
  );
}
